/**
 * What a pane is showing (RFC 0266 §3a) — shell state, not layout state, so it
 * lives beside the selected session rather than in useLayout.
 *
 * A SUBJECT, not an "open file". The old name said two untrue things: nothing
 * here is open (this is a resolved descriptor, and the pane may never render
 * it), and RFC 0281 §4 is explicit that files never become documents. A
 * document, an app instance and a web page are all subjects; only one of them
 * is anything like a file, and `path` below is a DISPLAY path for exactly that
 * reason.
 *
 * Deliberately RFC 0263's DocRef minus updatedAt: a documents sidebar opens one
 * with no adapter, and RFC 0264 checkout files fill the same shape from a path
 * plus a detected mime. Neither is a dependency of this slice.
 */

import { isWebId } from '../../features/web/webPage'

export interface Subject {
  id: string
  title: string
  /** A display path ("notes.md", "apps/board.html"), not a filesystem path. */
  path: string
  mime: string
  /**
   * RFC 0271: when `id` is an app INSTANCE, the app view that renders it.
   *
   * A document app's instances are addressed by their OWN id, like every other
   * document, so `?file=` carries the instance and this says what to draw it
   * with. The alternative considered was `?file=<view>&instance=<doc>`, which
   * encodes "you are opening the app, scoped to a record" — the
   * application-shaped framing RFC 0271 exists to reject. A Pages document is
   * addressed by its own path, not by `Pages.app?doc=`.
   *
   * Absent for a plain document and for an app opened by name, which is why
   * both paths are unchanged.
   */
  viewDocId?: string
  /**
   * The app this file belongs to, when it belongs to one (RFC 0274).
   *
   * The app's identity, not its artifact. `viewDocId` is a POINTER to whichever
   * document currently holds the built view, and under 0274 it churns: a
   * republish may mint a fresh document rather than rewriting one. Anything
   * that means "which app is this" must key on this field, or it silently
   * detaches the moment an app is rebuilt.
   */
  appId?: string
  /**
   * A record whose app is not installed here or in the World (RFC 0338 S7):
   * its data type. The pane offers to install the app instead of drawing it.
   */
  missingApp?: string
}

/**
 * Breadcrumbs for the canvas header (§3f). Legacy's adaptive-depth rule with
 * smaller inputs, since /w has no branches: with both side panels collapsed
 * there is room for provenance, so the project name leads; otherwise the
 * file's own path is enough. The last crumb is the file itself.
 */
export function fileBreadcrumbs(
  file: Subject,
  opts: {
    projectName?: string
    sidebarCollapsed: boolean
    agentCollapsed: boolean
  },
): string[] {
  // A web page's `path` is a url, and splitting it on `/` yields crumbs like
  // "https:" and "" — the segments of a url are not a hierarchy the way a
  // document's path is. The page shows its full url in its own chrome, so the
  // header says the one thing that is not already on screen: where you are.
  if (isWebId(file.id)) return [file.title]
  const dirs = file.path.split('/').filter((part) => part.length > 0)
  dirs.pop() // the filename is the last crumb, and title is its display form
  const crumbs = [...dirs, file.title]
  return opts.sidebarCollapsed && opts.agentCollapsed && opts.projectName
    ? [opts.projectName, ...crumbs]
    : crumbs
}

/**
 * Where an app's tile sits in the breadcrumbs (RFC 0332).
 *
 * An app opened by name has its name as a crumb, and the tile takes that
 * crumb's place rather than repeating it. A record of an app ("Lunch?" in
 * Polls) has no app crumb, so the tile leads the path as one. `appIndex` is -1
 * for a document that belongs to no app.
 */
export function withAppCrumb(
  breadcrumbs: string[],
  appName: string | undefined,
): { crumbs: string[]; appIndex: number } {
  if (!appName) return { crumbs: breadcrumbs, appIndex: -1 }
  const named = breadcrumbs.indexOf(appName)
  return named < 0
    ? { crumbs: [appName, ...breadcrumbs], appIndex: 0 }
    : { crumbs: breadcrumbs, appIndex: named }
}
